import {createSlice, createSelector} from '@reduxjs/toolkit'
import {getDisabledFilters, getFilterLabel} from '../../utils/filterManagementHelpers'

/**
 * Unified Filter Management Slice
 *
 * This slice manages all filter selections with a three-level structure:
 *
 * Level 1 - availableFilters: Filters user has permission to access (from API)
 *   Example: ['age', 'gender', 'race', 'occupation', 'hasVeteran']
 *
 * Level 2 - addedFilters: Filters currently visible in the drawer (user's choice)
 *   Example: ['age', 'gender']
 *
 * Level 3 - appliedFilters: Filters with actual values set (affecting data)
 *   Example: { age: [18, 65], gender: ['Female', 'Male'] }
 *
 * Relationships:
 * - availableFilters ⊇ addedFilters ⊇ keys(appliedFilters)
 * - Only available filters can be added
 * - Only added filters can have values
 */

const initialState = {
  availableFilters: [],
  addedFilters: [],
  appliedFilters: {},
  filterMetadata: {},
  disabledFilters: [],
  selectedState: null,
  lastProcessedStateArray: null, // Track last state to prevent duplicate resets
  isDirty: false,
  categoryFilterCounts: {},
  customizations: {
    filterNames: {},
    categoryNames: {}
  },
  outreachPermissions: {
    benefitsEligibility: [],
    workforceRecruitment: []
  },
  pendingFilterValues: {},
  removedAndResetFilters: {
    removed: [],
    reset: []
  }
}

const filterManagementSlice = createSlice({
  name: 'filterManagement',
  initialState,
  reducers: {
    /**
     * Set available filters (from API based on user permissions)
     */
    setAvailableFilters: (state, action) => {
      state.availableFilters = action.payload
    },

    /**
     * Add a filter to the drawer (makes it visible)
     */
    addFilter: (state, action) => {
      const {key, categoryId, type} = action.payload

      if (state.availableFilters.includes(key) && !state.addedFilters.includes(key)) {
        state.addedFilters.push(key)
        state.filterMetadata[key] = {
          categoryId,
          type,
          addedAt: Date.now()
        }
        state.isDirty = true
        updateCategoryFilterCounts(state)
      }
    },

    /**
     * Remove a filter from the drawer
     */
    removeAddedFilter: (state, action) => {
      const key = action.payload
      state.addedFilters = state.addedFilters.filter((k) => k !== key)
      delete state.appliedFilters[key]
      delete state.filterMetadata[key]
      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Toggle filter in drawer (add if not present, remove if present)
     */
    toggleFilter: (state, action) => {
      const {key, categoryId, type} = action.payload
      if (state.addedFilters.includes(key)) {
        state.addedFilters = state.addedFilters.filter((k) => k !== key)
        delete state.appliedFilters[key]
        delete state.filterMetadata[key]
      } else if (state.availableFilters.includes(key)) {
        state.addedFilters.push(key)
        state.filterMetadata[key] = {
          categoryId,
          type,
          addedAt: Date.now()
        }
      }
      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Set a filter value (only works if filter is added)
     */
    setFilterValue: (state, action) => {
      const {key, value} = action.payload

      if (!state.addedFilters.includes(key)) {
        console.warn(`Cannot set value for filter "${key}" - filter not added to drawer`)
        return
      }

      const metadata = state.filterMetadata[key]
      const filterType = metadata?.type
      const categoryId = metadata?.categoryId
      const shouldRemoveValue = isEmptyFilterValue(value, filterType)

      if (shouldRemoveValue) {
        delete state.appliedFilters[key]
      } else {
        state.appliedFilters[key] = value
        if (metadata) {
          metadata.lastModified = Date.now()
        }
        clearMutuallyExclusiveOutreachFilters(state, key, filterType, categoryId)
      }

      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Add multiple filters at once
     */
    addMultipleFilters: (state, action) => {
      const filters = action.payload // Array of {key, categoryId, type}
      for (const {key, categoryId, type} of filters) {
        if (state.availableFilters.includes(key) && !state.addedFilters.includes(key)) {
          state.addedFilters.push(key)
          state.filterMetadata[key] = {
            categoryId,
            type,
            addedAt: Date.now()
          }
        }
      }

      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Set multiple filter values at once
     */
    setMultipleFilterValues: (state, action) => {
      const filters = action.payload

      for (const [key, value] of Object.entries(filters)) {
        if (state.addedFilters.includes(key)) {
          const filterType = state.filterMetadata[key]?.type
          const isToggleAll = filterType === 'toggle' && value === 2

          if (value === null || value === undefined || isToggleAll) {
            delete state.appliedFilters[key]
          } else {
            state.appliedFilters[key] = value
            if (state.filterMetadata[key]) {
              state.filterMetadata[key].lastModified = Date.now()
            }
          }
        }
      }

      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Clear all filter values (keep filters in drawer)
     */
    clearAllFilterValues: (state) => {
      state.appliedFilters = {}
      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Clear all filters (remove from drawer and clear values)
     */
    clearAllFilters: (state) => {
      state.addedFilters = []
      state.appliedFilters = {}
      state.filterMetadata = {}
      state.categoryFilterCounts = {}
      state.isDirty = false
    },

    /**
     * Remove all added filters from drawer
     */
    removeAllAddedFilters: (state) => {
      state.addedFilters = []
      state.appliedFilters = {}
      state.filterMetadata = {}
      state.categoryFilterCounts = {}
      state.isDirty = true
    },

    /**
     * Clear filters for a specific category
     */
    clearCategoryFilters: (state, action) => {
      const categoryId = action.payload
      const filtersToRemove = []

      for (const [key, metadata] of Object.entries(state.filterMetadata)) {
        if (metadata.categoryId === categoryId) {
          filtersToRemove.push(key)
        }
      }

      for (const key of filtersToRemove) {
        state.addedFilters = state.addedFilters.filter((k) => k !== key)
        delete state.appliedFilters[key]
        delete state.filterMetadata[key]
      }

      state.isDirty = true
      updateCategoryFilterCounts(state)
    },

    /**
     * Set disabled filters based on business logic
     */
    setDisabledFilters: (state, action) => {
      state.disabledFilters = action.payload
    },

    /**
     * Add a filter to the disabled list
     */
    addDisabledFilter: (state, action) => {
      const key = action.payload
      if (!state.disabledFilters.includes(key)) {
        state.disabledFilters.push(key)
      }
    },

    /**
     * Remove a filter from the disabled list
     */
    removeDisabledFilter: (state, action) => {
      const key = action.payload
      state.disabledFilters = state.disabledFilters.filter((k) => k !== key)
    },

    /**
     * Set the currently selected state (affects filter availability)
     */
    setSelectedState: (state, action) => {
      state.selectedState = action.payload
    },

    /**
     * Reset dirty flag
     */
    resetDirtyFlag: (state) => {
      state.isDirty = false
    },

    /**
     * Manually set category filter counts (useful for initialization)
     */
    setCategoryFilterCounts: (state, action) => {
      state.categoryFilterCounts = action.payload
    },

    /**
     * Import/restore filter state (useful for saved views, personas, etc.)
     */
    importFilterState: (state, action) => {
      const {addedFilters, appliedFilters, filterMetadata} = action.payload
      state.addedFilters = addedFilters || []
      state.appliedFilters = appliedFilters || {}
      state.filterMetadata = filterMetadata || {}
      state.isDirty = false
      updateCategoryFilterCounts(state)
    },

    /**
     * Apply business rules to disable filters based on state selection
     */
    applyBusinessRules: (state, action) => {
      const {selectedState, config} = action.payload
      state.selectedState = selectedState
      state.disabledFilters = []

      // Apply all business rules
      applyChildcareRule(state, selectedState, config)
      applyGeographicFiltersRule(state, selectedState, config)
      applyNationalViewRule(state, selectedState, config)
      applyFilterDependenciesRule(state, selectedState, config)

      updateCategoryFilterCounts(state)
    },

    /**
     * Set customizations (custom names from backend)
     */
    setCustomizations: (state, action) => {
      state.customizations = action.payload
    },

    /**
     * Set outreach permissions from API
     * @param {Object} action.payload - { benefitsEligibility: number[], workforceRecruitment: number[] }
     */
    setOutreachPermissions: (state, action) => {
      state.outreachPermissions = {
        ...state.outreachPermissions,
        ...action.payload
      }
    },

    /**
     * Set a pending filter value (for dynamic filters from usecases)
     * Value will be applied when filter options are loaded
     * @param {Object} action.payload - { key: string, value: any }
     */
    setPendingFilterValue: (state, action) => {
      const {key, value} = action.payload
      state.pendingFilterValues[key] = value
    },

    /**
     * Set multiple pending filter values at once
     * @param {Object} action.payload - { filterKey: value, ... }
     */
    setPendingFilterValues: (state, action) => {
      for (const [key, value] of Object.entries(action.payload)) {
        state.pendingFilterValues[key] = value
      }
    },

    /**
     * Apply a pending filter value (move from pending to applied)
     * Called when filter options have finished loading
     * @param {string} action.payload - filter key
     */
    applyPendingFilterValue: (state, action) => {
      const key = action.payload

      if (key in state.pendingFilterValues) {
        const value = state.pendingFilterValues[key]

        if (state.addedFilters.includes(key)) {
          state.appliedFilters[key] = value
          if (state.filterMetadata[key]) {
            state.filterMetadata[key].lastModified = Date.now()
          }
        }

        delete state.pendingFilterValues[key]
        state.isDirty = true
        updateCategoryFilterCounts(state)
      }
    },

    /**
     * Clear a specific pending filter value
     * @param {string} action.payload - filter key
     */
    clearPendingFilterValue: (state, action) => {
      const key = action.payload
      delete state.pendingFilterValues[key]
    },

    /**
     * Clear all pending filter values
     */
    clearAllPendingFilterValues: (state) => {
      state.pendingFilterValues = {}
    },

    /**
     * Store fetched range options for a filter (to avoid re-fetching)
     * @param {Object} action.payload - { key: string, rangeOptions: { min, max, minLabel, maxLabel } }
     */
    setFilterRangeOptions: (state, action) => {
      const {key, rangeOptions} = action.payload
      if (state.filterMetadata[key]) {
        state.filterMetadata[key].rangeOptions = rangeOptions
      }
    },

    /**
     * Add a filter label to the removed list (for notification banner)
     * @param {string} action.payload - Filter label/name to add
     */
    addToFiltersRemoved: (state, action) => {
      const filterLabel = action.payload
      if (filterLabel && !state.removedAndResetFilters.removed.includes(filterLabel)) {
        state.removedAndResetFilters.removed.push(filterLabel)
      }
    },

    /**
     * Add a filter label to the reset list (for notification banner)
     * @param {string} action.payload - Filter label/name to add
     */
    addToFiltersReset: (state, action) => {
      const filterLabel = action.payload
      if (filterLabel && !state.removedAndResetFilters.reset.includes(filterLabel)) {
        state.removedAndResetFilters.reset.push(filterLabel)
      }
    },

    /**
     * Clear both removed and reset filter lists
     */
    clearFiltersRemovedAndReset: (state) => {
      state.removedAndResetFilters = {
        removed: [],
        reset: []
      }
    },

    /**
     * Set last processed state array (internal use by thunk)
     */
    setLastProcessedStateArray: (state, action) => {
      state.lastProcessedStateArray = action.payload
    }
  }
})

function removeFilterFromState(state, key) {
  state.addedFilters = state.addedFilters.filter((k) => k !== key)
  delete state.appliedFilters[key]
  delete state.filterMetadata[key]
}

function isEmptyFilterValue(value, filterType) {
  if (value === null || value === undefined) return true
  if (filterType === 'toggle' && value === '2') return true
  if (filterType === 'outreach' && (!value?.selected || value.selected.length === 0)) return true
  return false
}

function clearMutuallyExclusiveOutreachFilters(state, key, filterType, categoryId) {
  if (filterType !== 'outreach' || categoryId !== '8') return

  const outreachKeys = ['receivingBenefits', 'workforceRecruitment']
  for (const outreachKey of outreachKeys) {
    if (outreachKey !== key && state.appliedFilters[outreachKey]) {
      delete state.appliedFilters[outreachKey]
    }
  }
}

function disableAndRemoveFilter(state, key) {
  if (!state.disabledFilters.includes(key)) {
    state.disabledFilters.push(key)
  }
  removeFilterFromState(state, key)
}

function applyChildcareRule(state, selectedState, config) {
  if (!selectedState || !config?.childCareDisableState?.includes(selectedState)) return
  disableAndRemoveFilter(state, 'estimatedDrivingDistanceChildcare')
}

function applyGeographicFiltersRule(state, selectedState, config) {
  if (selectedState || !config?.disableCommunitySupportAndLocationFiltersKey) return

  for (const key of config.disableCommunitySupportAndLocationFiltersKey) {
    disableAndRemoveFilter(state, key)
  }
}

function applyNationalViewRule(state, selectedState, config) {
  const isNationalView =
    selectedState && config?.entireUnitedStateLabel && selectedState === config.entireUnitedStateLabel

  if (!isNationalView || !config?.disableCommunitySupportAndLocationFiltersKey) return

  for (const key of config.disableCommunitySupportAndLocationFiltersKey) {
    disableAndRemoveFilter(state, key)
  }
}

function applyFilterDependenciesRule(state, selectedState, config) {
  if (!config?.filterDependencies) return

  for (const [filterKey, dependencies] of Object.entries(config.filterDependencies)) {
    const dependenciesMet = dependencies.every((dep) => {
      if (dep === 'state') {
        return selectedState && selectedState !== config?.entireUnitedStateLabel
      }
      return true
    })

    if (!dependenciesMet) {
      disableAndRemoveFilter(state, filterKey)
    }
  }
}

function updateCategoryFilterCounts(state) {
  const counts = {}

  for (const key of state.addedFilters) {
    const metadata = state.filterMetadata[key]
    if (metadata) {
      const {categoryId} = metadata
      counts[categoryId] = (counts[categoryId] || 0) + 1
    }
  }

  state.categoryFilterCounts = counts
}

export const {
  setAvailableFilters,
  addFilter,
  removeAddedFilter,
  toggleFilter,
  setFilterValue,
  addMultipleFilters,
  setMultipleFilterValues,
  clearAllFilterValues,
  clearAllFilters,
  removeAllAddedFilters,
  clearCategoryFilters,
  setDisabledFilters,
  addDisabledFilter,
  removeDisabledFilter,
  setSelectedState,
  resetDirtyFlag,
  setCategoryFilterCounts,
  importFilterState,
  applyBusinessRules,
  setCustomizations,
  setOutreachPermissions,
  setPendingFilterValue,
  setPendingFilterValues,
  applyPendingFilterValue,
  clearPendingFilterValue,
  clearAllPendingFilterValues,
  setFilterRangeOptions,
  addToFiltersRemoved,
  addToFiltersReset,
  clearFiltersRemovedAndReset
} = filterManagementSlice.actions

/**
 * Thunk: Handle filter updates when state/geography changes
 *
 * This thunk performs three operations when state selection changes:
 * 1. Calculate and set disabled filters based on selected state(s)
 * 2. Remove disabled filters from drawer and track as "removed" for banner notification
 * 3. Reset geography-dependent filter values (category 6 & 7 range filters) and track as "reset"
 *
 * Using a thunk avoids the re-render loop caused by having state dependencies
 * while also dispatching multiple coordinated actions.
 *
 * @param {Array|string} selectedStateArray - Selected state(s)
 * @param {Object} filterConfig - Filter configuration object
 */
export const updateFiltersOnStateChange = (selectedStateArray, filterConfig) => (dispatch, getState) => {
  if (!filterConfig) return

  const {lastProcessedStateArray} = getState().filterManagement

  // Check if state actually changed by comparing values
  const currentStateString = JSON.stringify(selectedStateArray || [])
  const lastProcessedString = JSON.stringify(lastProcessedStateArray || [])

  if (lastProcessedString === currentStateString) {
    // State hasn't changed, skip processing
    return
  }

  // Update the last processed state
  dispatch({type: 'filterManagement/setLastProcessedStateArray', payload: selectedStateArray})

  const disabled = getDisabledFilters(selectedStateArray, filterConfig)
  dispatch(setDisabledFilters(disabled))

  const {addedFilters, customizations, filterMetadata} = getState().filterManagement

  for (const filterKey of disabled) {
    if (addedFilters.includes(filterKey)) {
      const filterLabel = getFilterLabel(filterKey, filterConfig, customizations)
      dispatch(addToFiltersRemoved(filterLabel))
      dispatch(removeAddedFilter(filterKey))
    }
  }

  // Reset range filters in geography-dependent categories (Community Support, Environmental Exposure)
  const resetCategoryIds = new Set(['6', '7'])
  for (const filterKey of addedFilters) {
    if (disabled.includes(filterKey)) continue

    const metadata = filterMetadata[filterKey]
    if (!metadata) continue

    const isGeographyDependent = resetCategoryIds.has(metadata.categoryId)
    const isRangeFilter = metadata.type === 'range'

    if (isGeographyDependent && isRangeFilter) {
      const filterLabel = getFilterLabel(filterKey, filterConfig, customizations)
      dispatch(addToFiltersReset(filterLabel))
      dispatch(setFilterValue({key: filterKey, value: null}))
    }
  }
}

export const selectAvailableFilters = (state) => state.filterManagement.availableFilters
export const selectAddedFilters = (state) => state.filterManagement.addedFilters
export const selectAppliedFilters = (state) => state.filterManagement.appliedFilters
export const selectFilterMetadata = (state) => state.filterManagement.filterMetadata
export const selectDisabledFilters = (state) => state.filterManagement.disabledFilters
export const selectSelectedState = (state) => state.filterManagement.selectedState
export const selectIsDirty = (state) => state.filterManagement.isDirty
export const selectCategoryFilterCounts = (state) => state.filterManagement.categoryFilterCounts
export const selectCustomizations = (state) => state.filterManagement.customizations
export const selectBenefitsEligibility = (state) => state.filterManagement.appliedFilters.receivingBenefits || null
export const selectWorkforceRecruitment = (state) => state.filterManagement.appliedFilters.workforceRecruitment || null
export const selectBenefitsPermissions = (state) =>
  state.filterManagement.outreachPermissions?.benefitsEligibility || []
export const selectWorkforcePermissions = (state) =>
  state.filterManagement.outreachPermissions?.workforceRecruitment || []
export const selectPendingFilterValues = (state) => state.filterManagement.pendingFilterValues
export const selectTotalAddedFiltersCount = (state) => state.filterManagement.addedFilters.length
export const selectTotalAppliedFiltersCount = (state) => Object.keys(state.filterManagement.appliedFilters).length
export const selectAddableFilters = createSelector(
  [selectAvailableFilters, selectAddedFilters, selectDisabledFilters],
  (availableFilters, addedFilters, disabledFilters) => {
    return availableFilters.filter((key) => !addedFilters.includes(key) && !disabledFilters.includes(key))
  }
)
export const selectRemovedAndResetFilters = (state) => state.filterManagement.removedAndResetFilters

export default filterManagementSlice.reducer
